import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Textarea } from 'primeng/textarea';

@Component({
  selector: 'app-document-form-table',
  imports: [ButtonModule, FormsModule, Textarea],
  standalone: true,
  templateUrl: './document-form-table.component.html',
  styleUrl: './document-form-table.component.scss'
})
export class DocumentFormTableComponent implements AfterViewInit {
  @ViewChildren('textarea') textareas!: QueryList<ElementRef<HTMLTextAreaElement>>;
  @Input() set cells(value: string[][] | string) {
    if (typeof value === 'string') {
      try {
        this._cells = JSON.parse(value);
      } catch (e) {
        this._cells = [['', '', ''], ['', '', ''], ['', '', '']];
      }
    } else if (Array.isArray(value)) {
      this._cells = value;
    } else {
      this._cells = [['', '', ''], ['', '', ''], ['', '', '']];
    }
  }
  get cells(): string[][] {
    return this._cells;
  }
  private _cells: string[][] = [['', '', ''], ['', '', ''], ['', '', '']];
  @Input() disabled: boolean = false;
  @Output() onCellChange: EventEmitter<{ rowIndex: number, colIndex: number, value: string }> = new EventEmitter();
  @Output() onUpdate: EventEmitter<{ cells: string[][] }> = new EventEmitter();
  @Output() onBlur: EventEmitter<{ cells: string[][] }> = new EventEmitter();

  hoveredColumnIndex: number | null = null
  hoveredRowIndex: number | null = null

  constructor() { }

  ngOnInit(): void {
    if (!this.cells || this.cells.length === 0 || this.cells[0].length === 0) {
      this.cells = [['', '', ''], ['', '', ''], ['', '', '']];
    }
  }

  ngAfterViewInit(): void {
    // Adjust heights of all textareas based on their content
    setTimeout(() => {
      this.adjustAllTextareaHeights();
    }, 0);

    // Subscribe to changes in textareas
    this.textareas.changes.subscribe(() => {
      setTimeout(() => {
        this.adjustAllTextareaHeights();
      }, 0);
    });
  }

  adjustAllTextareaHeights(): void {
    this.textareas.forEach((textareaRef) => {
      const textarea = textareaRef.nativeElement;
      textarea.style.height = 'auto';
      const minHeight = 40; // 2.5rem minimum height
      const maxHeight = 80; // 80px = 5rem
      const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
      textarea.style.height = newHeight + 'px';
    });
  }

  onCellUpdate(event: Event, rowIndex: number, colIndex: number) {
    const target = event.target as HTMLTextAreaElement;
    const value = target.value;

    // Auto-resize textarea based on content
    target.style.height = 'auto';
    const minHeight = 40; // 2.5rem minimum height for empty/short content
    const maxHeight = 80; // 80px = 5rem (3-4 lines)
    const newHeight = Math.max(minHeight, Math.min(target.scrollHeight, maxHeight));
    target.style.height = newHeight + 'px';

    this.onCellChange.emit({ rowIndex, colIndex, value });
    this.onUpdate.emit({ cells: this.cells });
  }

  addColumn(colIndex?: number) {
    let cells = this.cells;

    if (cells.length === 0 || cells[0].length === 0) {
      cells = [['']];
    }

    const targetColIndex = colIndex !== undefined ? colIndex : cells[0].length;
    for (let i = 0; i < cells.length; i++) {
      cells[i].splice(targetColIndex, 0, '');
    }
    this.cells = [...cells];
    this.onUpdate.emit({ cells });
  }

  removeColumn(colIndex: number) {
    const cells = this.cells;
    if (cells.length > 0 && cells[0].length > 1) {
      for (let i = 0; i < cells.length; i++) {
        cells[i].splice(colIndex, 1);
      }
      this.cells = [...cells];
      this.onUpdate.emit({ cells });
    } else {
      console.warn("Cannot remove the last column.");
    }
  }

  addRow(rowIndex?: number) {
    let cells = this.cells;

    if (cells.length === 0 || cells[0].length === 0) {
      cells = [['']];
    }

    const rowLength = cells[0]?.length || 1;
    const targetRowIndex = rowIndex !== undefined ? rowIndex : cells.length;
    cells.splice(targetRowIndex, 0, new Array(rowLength).fill(''));
    this.cells = [...cells];
    this.onUpdate.emit({ cells });
  }

  removeRow(rowIndex: number) {
    const cells = this.cells;
    if (cells.length > 1) {
      cells.splice(rowIndex, 1);
      this.cells = [...cells];
      this.onUpdate.emit({ cells });
    } else {
      console.warn("Cannot remove the last row.");
    }
  }

  onCellHover(rowIndex: number, colIndex: number): void {
    this.hoveredColumnIndex = colIndex;
    this.hoveredRowIndex = rowIndex;
  }

  onCellLeave(): void {
    this.hoveredColumnIndex = null;
    this.hoveredRowIndex = null;
  }

  updateCellValue(rowIndex: number, colIndex: number, value: Event) {
    const cells = this.cells;
    if (cells && cells[rowIndex] && cells[rowIndex][colIndex] !== undefined) {
      cells[rowIndex][colIndex] = (value.target as HTMLInputElement).value;
      this.cells = [...cells];
    }
  }
}
